import { useState } from "react";
import {
  Text,
  View,
  ScrollView,
  ActivityIndicator,
  Linking,
  Pressable,
} from "react-native";
import { Link } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { Avatar } from "@/shared/components/ui/avatar";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Pill } from "@/shared/components/ui/pill";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { useProfile } from "@/features/profile/api/profile";
import { UserEvents } from "@/features/profile/components/user-events";
import { FavoriteTags } from "@/features/profile/components/favorite-tags";
import { useUserEvents } from "@/features/events/api/event-participants";
import { useFollowStatus } from "@/features/profile/api/followers";
import { ProfileStatsBar } from "@/features/profile/components/profile-stats-bar";
import { ShareProfileButton } from "@/features/profile/components/share-profile-button";
import { AvatarPreviewModal } from "@/features/profile/components/avatar-preview-modal";
import { useTabBarHeight } from "@/shared/hooks/use-tab-bar-height";
import { InviteCallout } from "@/features/callouts";

export default function ProfileScreen() {
  const { session } = useAuthContext();
  const { data: profile, isLoading, error } = useProfile(session?.user.id);
  const { data: userEvents } = useUserEvents(session?.user.id ?? "");
  const { data: followStatus } = useFollowStatus(session?.user.id ?? "");
  const [previewVisible, setPreviewVisible] = useState(false);

  const pink = useCSSVariable("--color-pink") as string;
  const foreground = useCSSVariable("--color-foreground") as string;
  const tabBarHeight = useTabBarHeight();

  if (isLoading) {
    return (
      <ScreenContainer
        className="items-center justify-center p-6"
        bottomInset={false}
      >
        <ActivityIndicator size="large" className="text-brand" />
      </ScreenContainer>
    );
  }

  if (error || !profile) {
    return (
      <ScreenContainer
        className="items-center justify-center p-6"
        bottomInset={false}
      >
        <Text className="text-brand text-center text-base">
          Failed to load profile
        </Text>
      </ScreenContainer>
    );
  }

  const locationParts = [profile.city, profile.state].filter(Boolean);
  const location =
    locationParts.length > 0
      ? `${locationParts.join(" - ")}`
      : profile.country || "";

  return (
    <ScrollView
      contentContainerClassName="grow"
      contentContainerStyle={{ paddingBottom: tabBarHeight }}
      className="bg-background flex-1"
      contentInsetAdjustmentBehavior="never"
    >
      <ScreenContainer minTopPadding={24} bottomInset={false}>
        {/* Header with username and settings button */}
        <View className="flex-row items-center justify-between px-4 pt-0 pb-6">
          <View className="h-10 w-10" />
          <Text className="text-muted-foreground text-lg">
            @{profile.username}
          </Text>
          <Link asChild href={"/user/settings"}>
            <IconButton
              shape="circle"
              surface="transparent"
              accessibilityLabel="Settings"
            >
              <Ionicons name="settings-outline" size={24} color={foreground} />
            </IconButton>
          </Link>
        </View>

        <View className="px-6">
          <InviteCallout placement="profile" className="mb-6" />
        </View>

        {/* Profile Section */}
        <View className="px-6 pb-6">
          <View className="flex-row items-center gap-4">
            {/* Profile Picture */}
            <Pressable
              onPress={() => setPreviewVisible(true)}
              disabled={!profile.avatarUrl}
              accessibilityRole="imagebutton"
              accessibilityLabel="Ampliar foto de perfil"
            >
              <Avatar
                url={profile.avatarUrl}
                name={profile.displayName || profile.username}
                size={88}
              />
            </Pressable>
            <AvatarPreviewModal
              visible={previewVisible}
              onClose={() => setPreviewVisible(false)}
              url={profile.avatarUrl}
              name={profile.displayName || profile.username}
            />

            {/* Name, Location, Edit + Instagram */}
            <View className="flex-1 gap-1">
              <Text
                className="text-foreground font-display font-semibold"
                style={{ fontSize: 20 }}
              >
                {profile.displayName || profile.username}
              </Text>
              {location && (
                <Text className="text-muted-foreground text-sm">
                  {location}
                </Text>
              )}
              <View className="mt-2 flex-row items-center gap-2">
                <Link asChild href={"/user/edit"} className="flex-1">
                  <Pill
                    label="Editar perfil"
                    variant="outline"
                    tone="neutral"
                    leftIcon={
                      <Ionicons name="pencil" size={14} color={foreground} />
                    }
                    className="flex-1"
                    accessibilityLabel="Editar perfil"
                  />
                </Link>
                <ShareProfileButton
                  userId={profile.userId}
                  displayName={profile.displayName || profile.username}
                />
                {profile.instagramUsername && profile.shouldShowInstagram && (
                  <IconButton
                    size="sm"
                    shape="circle"
                    surface="tinted"
                    tone="pink"
                    accessibilityLabel={`Instagram de ${profile.displayName || profile.username}`}
                    accessibilityRole="link"
                    onPress={() =>
                      Linking.openURL(
                        `https://instagram.com/${profile.instagramUsername}`
                      )
                    }
                  >
                    <Ionicons name="logo-instagram" size={22} color={pink} />
                  </IconButton>
                )}
              </View>
            </View>
          </View>
        </View>

        <ProfileStatsBar
          followerCount={followStatus?.followerCount ?? 0}
          followingCount={followStatus?.followingCount ?? 0}
          eventCount={userEvents?.length ?? 0}
          userId={profile.userId ?? ""}
        />

        {/* User Events */}
        {profile.userId && (
          <View className="mt-4 px-6">
            <UserEvents userId={profile.userId} />
          </View>
        )}

        {/* Favorite Tags */}
        {profile.userId && (
          <View className="mt-4 px-6">
            <FavoriteTags userId={profile.userId} />
          </View>
        )}
      </ScreenContainer>
    </ScrollView>
  );
}
